import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  cloneAgent,
  updateAgentConfig,
  getAgent,
  getAgents,
  getBusinessAgents,
  getAgentTemplates,
  updateAgent,
  getDataSet,
  updateDataSet,
  createDataSet,
  deleteDataSet,
  getDataSets,
  getAgentStatus,
  getAmbientSounds,
  getAgentPronunciationRules,
  deleteAgentPronunciationRule,
  getAgentPronunciationRule,
  updateAgentPronunciationRule,
  createAgentPronunciationRule,
  getThinkingSounds
} from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { AgentResponse, AmbientSound, AgentStatusResponse, PronunciationRule, ThinkingSound } from './types';
import * as urls from 'routes/config';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { DatasetResponse } from '../setting/knowledgeBase/types';

export function useGetAgents(
  selectedLocationId?: number | null,
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AgentResponse[]; meta: MetaApiResponse }, Error>
) {
  const { locationId } = useParams();
  const workspaceId = selectedLocationId ?? locationId;

  return useQuery<{ data: AgentResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agents', workspaceId, params],
    queryFn: () => getAgents(String(workspaceId!), params),
    enabled: Boolean(workspaceId),
    ...options
  });
}

/** Agents across all locations for the current business (telephony assignment dropdowns). */
export function useGetBusinessAgents(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AgentResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: AgentResponse[]; meta: MetaApiResponse }>({
    queryKey: ['business_agents', params],
    queryFn: () => getBusinessAgents(params),
    ...options
  });
}

export function useGetAmbientSounds(options?: UseQueryOptions<{ data: AmbientSound[] }, Error>) {
  return useQuery<{ data: AmbientSound[] }>({
    queryKey: ['ambient_voices'],
    queryFn: () => getAmbientSounds(),
    ...options
  });
}

export function useGetThinkingSounds(options?: UseQueryOptions<{ data: ThinkingSound[] }, Error>) {
  return useQuery<{ data: ThinkingSound[] }>({
    queryKey: ['thinking_voices'],
    queryFn: () => getThinkingSounds(),
    ...options
  });
}

export function useGetAgent(options?: UseQueryOptions<AgentResponse, Error>) {
  const { locationId, agentId } = useParams();

  return useQuery<AgentResponse>({
    queryKey: ['agent', locationId, agentId],
    queryFn: () => getAgent(locationId!, agentId!),
    enabled: Boolean(agentId && locationId),
    ...options
  });
}

export function useGetAgentStatus(options?: UseQueryOptions<AgentStatusResponse, Error>) {
  const { locationId, agentId } = useParams();

  return useQuery<AgentStatusResponse>({
    queryKey: ['agent_status', locationId, agentId],
    queryFn: () => getAgentStatus(locationId!, agentId!),
    enabled: false,
    ...options
  });
}

export function useUpdateAgent() {
  const { locationId, agentId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateAgent(locationId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agents', locationId],
        refetchType: 'all'
      });
      if (agentId) {
        queryClient.invalidateQueries({
          queryKey: ['agent', locationId, agentId],
          refetchType: 'all'
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['business_phone_numbers'],
        refetchType: 'all'
      });
    }
  });
}

const invalidateAgentRelatedQueries = (locationId: string, agentId: string) => {
  const queryKeys = [
    ['performance', locationId, agentId],
    ['callLogs', locationId, agentId],
    ['signals', locationId, agentId],
    ['agent', locationId, agentId],
    ['worksheets', locationId, agentId],
    ['qaChunks', locationId, agentId],
    ['agentActions', locationId, agentId]
  ];

  queryKeys.forEach((queryKey) => {
    queryClient.invalidateQueries({
      queryKey,
      refetchType: 'all'
    });
  });
};

export function useUpdateAgentMode() {
  const { locationId, agentId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateAgent(locationId!, agentId!, payload),
    onSuccess: () => {
      invalidateAgentRelatedQueries(locationId!, agentId!);
    }
  });
}

export function useUpdateAgentConfig() {
  const { locationId, agentId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => updateAgentConfig(locationId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, agentId],
        refetchType: 'active'
      });
    }
  });
}

export function useGetAgentTemplates(
  params?: any,
  options?: UseQueryOptions<{ data: AgentResponse[]; meta: MetaApiResponse }, Error>
) {
  const { locationId } = useParams();

  return useQuery<{ data: AgentResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agent_templates', locationId, params],
    queryFn: () => getAgentTemplates(locationId!, params),
    ...options
  });
}

export function useCloneAgent() {
  const navigate = useNavigate();
  const { locationId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => cloneAgent(locationId!, payload),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: ['agents', locationId],
          refetchType: 'all'
        });
        localStorage.setItem('configuringAgent', 'true');
        navigate(`${urls.agentBase(locationId, data.id)}/${urls.home.configure}`);
      }
    }
  });
}

export function useGetAgentDataSets(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: DatasetResponse[]; meta: MetaApiResponse }, Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<{ data: DatasetResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agentDatasets', locationId, agentId, params],
    queryFn: () => getDataSets(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useGetAgentDataSet(options?: UseQueryOptions<DatasetResponse, Error>) {
  const { locationId, agentId, datasetId } = useParams<{
    locationId: string;
    agentId: string;
    datasetId: string;
  }>();

  return useQuery<DatasetResponse>({
    queryKey: ['agentDataset', locationId, agentId, datasetId],
    queryFn: () => getDataSet(locationId!, agentId!, datasetId!),
    enabled: Boolean(locationId && agentId && datasetId),
    ...options
  });
}

export function useCreateOrUpdateAgentDataSet() {
  const navigate = useNavigate();
  const { locationId, agentId, datasetId } = useParams<{
    locationId: string;
    agentId: string;
    datasetId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) =>
      datasetId
        ? updateDataSet(locationId!, agentId!, datasetId!, payload)
        : createDataSet(locationId!, agentId!, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', locationId, agentId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, agentId],
        refetchType: 'active'
      });
      if (datasetId) {
        queryClient.invalidateQueries({
          queryKey: ['agentDataset', locationId, agentId, datasetId],
          refetchType: 'all'
        });
      }
      if (data !== undefined) {
        navigate(-1);
      }
    }
  });
}

export function useUpdateAgentDataSet() {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  return useMutation({
    mutationFn: ({ datasetId, payload }: { datasetId: number; payload: any }) =>
      updateDataSet(locationId!, agentId!, String(datasetId!), payload),
    onSuccess: (data, variables) => {
      const { datasetId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', datasetId],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteAgentDataSet() {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteDataSet(locationId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentDatasets', locationId, agentId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, agentId],
        refetchType: 'active'
      });
    }
  });
}
export function useGetPronunciationRules(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: PronunciationRule[]; meta: MetaApiResponse }, Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<{ data: PronunciationRule[]; meta: MetaApiResponse }>({
    queryKey: ['agentPronunciationRules', locationId, agentId, params],
    queryFn: () => getAgentPronunciationRules(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useGetPronunciationRule(id: number, options?: UseQueryOptions<PronunciationRule, Error>) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<PronunciationRule>({
    queryKey: ['agentPronunciationRule', locationId, agentId, id],
    queryFn: () => getAgentPronunciationRule(locationId!, agentId!, id),
    enabled: Boolean(locationId && agentId && id),
    ...options
  });
}

export function useDeleteAgentPronunciationRule() {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: number) => deleteAgentPronunciationRule(locationId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentPronunciationRules', locationId, agentId]
      });
    }
  });
}

export function useUpdateAgentPronunciationRule() {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();

  return useMutation({
    mutationFn: (payload: any) => updateAgentPronunciationRule(locationId!, agentId!, payload?.id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentPronunciationRules', locationId, agentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['agentPronunciationRule', locationId, agentId]
      });
    }
  });
}

export function useCreateAgentPronunciationRule() {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();

  return useMutation({
    mutationFn: (payload: PronunciationRule) => createAgentPronunciationRule(locationId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentPronunciationRules', locationId, agentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['agentPronunciationRule', locationId, agentId]
      });
    }
  });
}
